import { faAngleRight } from '@fortawesome/pro-solid-svg-icons';

import { Card, Icon, Skeleton, Text, cn } from '@growtherapy/sprout-ui';

import { useTheme } from '../../hooks';
import { Theme } from '../../state';
import { PROGRESS_NOTE_CTA, PROGRESS_NOTE_SECTION_TITLE } from './constants';
import { formatRange } from './utils';

export type ProgressNoteSectionProps = {
  className?: string;
  state?: 'loading' | 'error' | 'default';
  startTime: Date | null;
  endTime: Date | null;
  onClick: () => void;
};

function LoadingSkeleton() {
  return <Skeleton className='h-10 w-full m-0' />;
}

function ProgressNoteSectionButton({
  onClick,
  startTime,
  endTime,
}: Pick<ProgressNoteSectionProps, 'onClick' | 'endTime' | 'startTime'>) {
  const hasStartAndEndTime = !!startTime && !!endTime;
  const duration = hasStartAndEndTime && formatRange(startTime, endTime);

  return (
    <button
      className='relative flex w-full justify-between items-center text-start hover:bg-neutral-400 rebrand:hover:bg-surface-inverseEmphasis'
      aria-label='Open progress note'
      onClick={onClick}
    >
      <div>
        {duration && (
          <Text as='span' className='block text-neutral-700 font-medium rebrand:text-text-inverse' variant='label'>
            {duration}
          </Text>
        )}
        <Text as='span' className='block rebrand:text-text-inverse'>
          {PROGRESS_NOTE_CTA}
        </Text>
      </div>
      <Icon icon={faAngleRight} className='mr-2 flex items-center justify-center rebrand:text-text-inverse' />
    </button>
  );
}

function ProgressNoteSectionContent({ state, ...buttonProps }: Omit<ProgressNoteSectionProps, 'className'>) {
  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;

  switch (state) {
    case 'loading':
      return <LoadingSkeleton />;
    case 'error':
      return (
        <Text
          className={cn('text-coral-700', {
            'rebrand:text-text-inverse': isDarkTheme,
          })}
        >
          Error loading progress note
        </Text>
      );
    default:
      return <ProgressNoteSectionButton {...buttonProps} />;
  }
}

export function ProgressNoteSection({ className, ...contentProps }: ProgressNoteSectionProps) {
  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;

  return (
    <Card
      title={PROGRESS_NOTE_SECTION_TITLE}
      titleAs='h3'
      className={cn(
        'flex flex-col m-0',
        {
          'rebrand:bg-surface-inverseEmphasis': isDarkTheme,
        },
        className,
      )}
      titleClassName={cn({
        'rebrand:text-text-inverse': isDarkTheme,
      })}
      data-testid='progress-note-section'
    >
      <ProgressNoteSectionContent {...contentProps} />
    </Card>
  );
}
